
// ==================== CONSTANTS ====================
export const ANIMATION_DURATION = {
  fast: 200,
  normal: 300,
  slow: 600,
};
export const BREAKPOINTS = {
  mobile: 600,
  tablet: 960,
  desktop: 1280,
};
export const COLORS = {
  primary: "#2196F3",
  primaryLight: "#64B5F6",
  primaryDark: "#1976D2",
  success: "#4CAF50",
  successLight: "#81C784",
  successDark: "#388E3C",
  secondary: "#FF9800",
  secondaryLight: "#FFB74D",
  error: "#F44336",
  warning: "#FF9800",
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
  },
  text: {
    primary: "#212121",
    secondary: "#757575",
  },
  background: "#FFFFFF",
  divider: "#E0E0E0",
};

// ==================== STYLES ====================
export const styles = {
  root: {
    width: "100%",
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${COLORS.primaryLight}11 0%, ${COLORS.secondaryLight}22 100%)`,
    padding: "24px 16px 64px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  },

  glassCard: {
    marginBottom: 24,
    borderRadius: 24,
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    transition: `box-shadow ${ANIMATION_DURATION.normal}ms, transform ${ANIMATION_DURATION.fast}ms`,
    position: "relative",
    overflow: "hidden",
    padding: "15px 24px",
    cursor: "default",
    border: "1px solid rgba(255,255,255,0.5)",
  },

  glassCardHover: {
    transform: "translateY(-2px) scale(1.01)",
  },

  sectionHeader: {
    margin: "0px 0px 20px",
    width: "100%",
  },

  chip: {
    display: "inline-flex",
    alignItems: "center",
    margin: 4,
    padding: "6px 16px",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 16,
    boxShadow: "0 1px 4px 0 rgba(33,150,243,0.08)",
    transition: `all ${ANIMATION_DURATION.fast}ms`,
    cursor: "default",
    gap: 6,
  },

  chipSuccess: {
    background: COLORS.success,
    color: "#fff",
  },

  chipDefault: {
    background: COLORS.grey[200],
    color: COLORS.text.primary,
    border: `1px solid ${COLORS.grey[300]}`,
  },

  chipWarning: {
    background: COLORS.warning,
    color: "#fff",
  },

  chipHover: {
    transform: "scale(1.05)",
  },

  progressContainer: {
    marginBottom: 16,
  },

  progressBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: COLORS.grey[200],
    overflow: "hidden",
    position: "relative",
  },

  progressFill: {
    height: "100%",
    borderRadius: 5,
    transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
    background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
  },

  progressFillSuccess: {
    background: `linear-gradient(90deg, ${COLORS.success} 0%, ${COLORS.successDark} 100%)`,
  },

  avatar: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // fontWeight: 700,
    // fontSize: "1.5rem",
    transition: `all ${ANIMATION_DURATION.normal}ms cubic-bezier(.4,0,.2,1)`,
    cursor: "pointer",
    flexShrink: 0,
  },

  avatarCurrent: {
    background: `linear-gradient(135deg, ${COLORS.success} 60%, ${COLORS.successLight} 100%)`,
    color: "#fff",
    boxShadow: `0 0 0 6px ${COLORS.success}33`,
  },

  avatarAchieved: {
    background: `linear-gradient(135deg, ${COLORS.primary} 60%, ${COLORS.primaryLight} 100%)`,
    color: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },

  avatarFuture: {
    background: COLORS.grey[200],
    color: COLORS.text.secondary,
  },

  accordion: {
    background: "rgba(255,255,255,0.95)",
    borderRadius: 16,
    marginBottom: 10,
    overflow: "hidden",
    transition: `all ${ANIMATION_DURATION.normal}ms`,
    border: `1px solid ${COLORS.divider}`,
  },

  accordionCurrent: {
    background: `linear-gradient(90deg, ${COLORS.successLight}22 0%, #fff 100%)`,
    boxShadow: `0 0 24px 0 ${COLORS.successLight}55`,
    border: `2px solid ${COLORS.success}`,
  },

  accordionHeader: {
    padding: "10px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 72,
    transition: `background ${ANIMATION_DURATION.fast}ms`,
    userSelect: "none",
  },

  accordionHeaderHover: {
    background: "rgba(0,0,0,0.02)",
  },

  accordionContent: {
    padding: "10px 20px 20px",
    transition: `all ${ANIMATION_DURATION.normal}ms`,
  },

  alert: {
    padding: "12px 16px",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
  },

  alertSuccess: {
    background: `${COLORS.successLight}22`,
    border: `1px solid ${COLORS.successLight}`,
    color: COLORS.successDark,
  },

  alertInfo: {
    background: `${COLORS.primaryLight}22`,
    border: `1px solid ${COLORS.primaryLight}`,
    color: COLORS.primaryDark,
  },

  alertWarning: {
    background: `${COLORS.warning}22`,
    border: `1px solid ${COLORS.warning}`,
    color: "#F57C00",
  },

  button: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    cursor: "pointer",
    transition: `all ${ANIMATION_DURATION.fast}ms`,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: COLORS.primary,
    color: "#fff",
  },

  buttonHover: {
    background: COLORS.primaryDark,
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(33,150,243,0.3)",
  },

  skeleton: {
    background: `linear-gradient(90deg, ${COLORS.grey[200]} 25%, ${COLORS.grey[100]} 50%, ${COLORS.grey[200]} 75%)`,
    backgroundSize: "200% 100%",
    animation: "loading 1.5s infinite",
    borderRadius: 8,
  },
};

// ==================== DATA STRUCTURES ====================
// ==================== RANKS DATA WITH IMAGES ====================
export const RANKS_DATA = [
  {
    id: 1,
    name: "Viva Retailer",
    image_url: "https://new-system-public.s3.amazonaws.com/uploads/rank-images/RIRINDD-1701328271109-2.png",
    level: 0,
    requirements: [],
    rewards: [],
    maintenance: [],
  },
  {
    id: 2,
    name: "Star Distributor",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRSTRD-1701328327546-1.png",
    level: 1,
    requirements: [
      "Retail 300 PV products in a cycle",
      "Maximum 100 PV per unique customer",
      "Need 3+ unique customers",
      "Can qualify in 1 week if criteria met",
    ],
    rewards: ["Entry into distributor network", "Access to wholesale pricing"],
    maintenance: ["Retail 75 PV in qualifying week"],
  },
  {
    id: 3,
    name: "Star (250) Distributor",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRST250-1701328736871-3.png",
    level: 2,
    requirements: ["Must be a Star", "One personally sponsored active Star in each organization (2 total)"],
    rewards: ["Enhanced commission structure", "Team building bonuses"],
    maintenance: ["One personally paid as Star in each organization"],
  },
  {
    id: 4,
    name: "Star (500) Distributor",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRST500-1701329429275-8.png",
    level: 3,
    requirements: [
      "Must be active Star 250",
      "Retail 600 PV",
      "5 personally sponsored active Stars",
      "At least 1 Star in each organization",
      "Maximum 100 PV per unique customer (need 6+ customers)",
      "Retail volume considered only from week after achieving Star rank",
    ],
    rewards: ["Accidental Insurance: ₹5 Lakhs coverage", "Leadership development programs"],
    maintenance: [
      "Must be active",
      "5 paid as personally sponsored Stars (at least 1 in each organization)",
      "Retail 75 PV in qualifying week",
    ],
  },
  {
    id: 17,
    name: "Star (1000) Distributor",
    image_url:
      "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRST1000-1701330138099-7.png",
    level: 4,
    requirements: [
      "Must be active Star 500",
      "Minimum 25 Stars or above (including 1 active Star 500 in each organization)",
      "Both active and inactive Stars count",
      "Excludes Stars with sales order hold, suspended, vacated, resigned, or terminated status",
    ],
    rewards: ["Star Summit", "Exclusive training events", "Recognition awards"],
    maintenance: ["Must be active", "1 active Star 500 and 750 GV in both organizations in qualifying week"],
  },
  {
    id: 5,
    name: "Ruby Executive",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIREXR-1701329459077-5.png",
    level: 5,
    requirements: [
      "Activity Level: 50 PV",
      "Group Volume: 1,000 GV each in both organizations",
      "Period: 2 weeks",
      "Aggregate Revenue: 2,500 GBU",
      "Averaging applicable (50% volume in all qualifying weeks)",
    ],
    rewards: ["Travel: Theatric Thailand (15-month timeline)", "Rank Maintenance Revenue: 50 GBU per week"],
    maintenance: [
      "50 PV active",
      "1,000 GV each in both Placement Trees",
      "500 GV each in 2 Personal Enrollment Trees",
    ],
  },
  {
    id: 6,
    name: "Pearl Executive",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIREXP-1701329486882-3.png",
    level: 6,
    requirements: [
      "Activity Level: 50 PV",
      "Group Volume: 2,500 GV each in both organizations",
      "Period: 2 weeks",
      "Aggregate Revenue: 7,500 GBU",
      "Averaging applicable",
    ],
    rewards: ["Travel: Magnificent Malaysia (15-month timeline)", "Rank Maintenance Revenue: 100 GBU per week"],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 7,
    name: "Emerald Executive",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIREXE-1701329516675-4.png",
    level: 7,
    requirements: [
      "Activity Level: 100 PV",
      "Group Volume: 5,000 GV each in both organizations",
      "Period: 2 weeks",
      "Aggregate Revenue: 20,000 GBU",
      "Averaging applicable",
    ],
    rewards: ["Travel: Unmatched Uzbekistan (15-month timeline)", "Rank Maintenance Revenue: 150 GBU per week"],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 8,
    name: "Sapphire Executive",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIREXS-1701329557883-6.png",
    level: 8,
    requirements: [
      "Activity Level: 100 PV",
      "Group Volume: 7,500 GV each in both organizations",
      "Period: 2 weeks",
      "Aggregate Revenue: 50,000 GBU",
      "Averaging applicable",
    ],
    rewards: ["Travel: Dazzling Dubai (15-month timeline)", "Rank Maintenance Revenue: 200 GBU per week"],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 9,
    name: "Executive Diamond",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRDEX-1701329592038-7.png",
    level: 9,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "Group Volume: 10,000 GV each in both organizations",
      "Period: 3 weeks",
      "Aggregate Revenue: 100,000 GBU",
      "Averaging applicable",
    ],
    rewards: [
      "Travel: Spectacular Singapore and Star cruise (15-month timeline)",
      "Rank Maintenance Revenue: 250 GBU per week",
      "Co-applicant Revenue: 500 GBU per month for 12 months",
    ],
    maintenance: ["200 PV/PCM active", "10,000 GV in both organizations in qualifying week"],
  },
  {
    id: 10,
    name: "Executive Blue Diamond",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRDEB-1701329616083-3.png",
    level: 10,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "Group Volume: 15,000 GV each in both organizations",
      "Period: 3 weeks",
      "Aggregate Revenue: 150,000 GBU",
      "Averaging applicable",
    ],
    rewards: [
      "Travel: Ravishing Russia (15-month timeline)",
      "Rank Maintenance Revenue: 300 GBU per week",
      "Co-applicant Revenue: Extended +12 months",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 11,
    name: "Executive Purple Diamond",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRDPD-1701329650574-8.png",
    level: 11,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "Group Volume: 20,000 GV each in both organizations",
      "Period: 3 weeks",
      "Aggregate Revenue: 200,000 GBU",
      "Averaging applicable",
    ],
    rewards: [
      "Travel: Charming China (15-month timeline)",
      "Rank Maintenance Revenue: 350 GBU per week",
      "Co-applicant Revenue: Extended +12 months",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 12,
    name: "Executive White Diamond",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRDEW-1701329680167-4.png",
    level: 12,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "Group Volume: 25,000 GV each in both organizations",
      "Period: 3 weeks",
      "Aggregate Revenue: 250,000 GBU",
      "Averaging applicable",
    ],
    rewards: [
      "Travel: Stunning South Africa (15-month timeline)",
      "Rank Maintenance Revenue: 400 GBU per week",
      "Co-applicant Revenue: Extended +12 months",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 13,
    name: "Black Diamond Ambassador",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRABD-1701329707995-2.png",
    level: 13,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "Group Volume: 50,000 GV each in both organizations",
      "Period: 4 weeks",
      "Aggregate Revenue: Not specified",
    ],
    rewards: [
      "Travel: Exuberant Europe (15-month timeline)",
      "Rank Maintenance Revenue: 500 GBU per week",
      "Co-applicant Revenue: Extended +60 months",
      "Single Team Revenue: 5% of GV from berth organizations (5,000 GBU cap each)",
      "Berth Organizations: Can open unlimited parallel organizations",
      "Royalty Eligibility: Share in global royalty pool",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 14,
    name: "Royal Black Diamond Ambassador",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRARBD-1701329736063-7.png",
    level: 14,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "100,000 PV each from 1st, 2nd organizations",
      "All berth organizations combined for 4 consecutive weeks",
      "Period: 4 weeks",
      "Aggregate Revenue: Not specified",
    ],
    rewards: [
      "Travel: Amazing Australia (5-year timeline)",
      "Rank Maintenance Revenue: 1,000 GBU per week",
      "Co-applicant Revenue: Lifetime",
      "Enhanced Royalty: Higher royalty share",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 15,
    name: "Imperial Black Diamond Ambassador",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIREIDB-1701329774940-1.png",
    level: 15,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "100,000 PV each from 1st, 2nd organizations",
      "200,000 PV from all berth organizations (max 100,000 from single berth)",
      "Period: 4 weeks",
      "Aggregate Revenue: Not specified",
    ],
    rewards: [
      "Travel: Unique USA (5-year timeline)",
      "Rank Maintenance Revenue: 1,500 GBU per week",
      "Enhanced Benefits: Premium royalty and recognition",
    ],
    maintenance: ["Same criteria as achievement with weekly volume requirements"],
  },
  {
    id: 16,
    name: "Crown Black Diamond Ambassador",
    image_url: "https://new-system-public.s3.ap-south-1.amazonaws.com/uploads/rank-images/RIRECDB-1701330106223-2.png",
    level: 16,
    requirements: [
      "Activity Level: 200 PV/PCM",
      "100,000 PV each from 1st, 2nd organizations",
      "300,000 PV from all berth organizations (max 100,000 from single berth)",
      "Period: 4 weeks",
      "Aggregate Revenue: Not specified",
    ],
    rewards: [
      "Travel: Top-notch tour to dream destination (5-year timeline)",
      "Rank Maintenance Revenue: 2,500 GBU per week",
      "Ultimate Recognition: Highest achiever status",
    ],
    maintenance: ["200 PV/PCM active", "100,000 GV in five organizations in qualifying week"],
  },
];


